<script lang="ts">
  import { onMount } from 'svelte';
  import { writable } from 'svelte/store';
  import { Skeleton } from '$lib/components/ui/skeleton';
  import { Switch } from '$lib/components/ui/switch';
  import SeasonControls from './SeasonControls.svelte';
  import SeasonCard from './SeasonCard.svelte';
  import { getMySeasonsData } from '@/Util/api';
  import { debouncer } from '@/Util/utils';
  import { loadSettings, loadView, saveSettings, saveView, type ChartView, type Metric, type SeasonsResponse, type SigKey } from './seasons';

  export let distanceUnits = 'km';

  // Shared by every chart so guides stay in unison, and a click sets the same checkpoint in every table.
  const hover = writable<{ x: number | null; from: string }>({ x: null, from: '' });
  // Reads sessionStorage, writing the defaults (January to the current month) back on first open.
  let { startMonth, endMonth, checkpoint: storedCheckpoint } = loadSettings();
  const checkpoint = writable<number | null>(storedCheckpoint);
  // Persist the checkpoint with the months; also runs once now, so the first open saves the defaults.
  checkpoint.subscribe((x) => saveSettings(startMonth, endMonth, x));
  let selectedYears: number[] | undefined; // undefined: let the server pick the default years on first load
  let yearsAvailable: number[] = [];
  let nextYearAvailable: number | null = null;
  let data: SeasonsResponse | null = null;
  let loading = true;
  let error: string | null = null;
  let requestId = 0;

  // signatureTitle: the card's title when the Fitness Signature view plots its predicted signature value instead.
  const cards: { title: string; signatureTitle?: string; metric: Metric; sigKey: SigKey | null }[] = [
    { title: 'Total Training Load', metric: 'tl', sigKey: null },
    { title: 'Low Training Load', signatureTitle: 'Threshold Power (TP)', metric: 'low', sigKey: 'tp' },
    { title: 'High Training Load', signatureTitle: 'High Intensity Energy (HIE)', metric: 'high', sigKey: 'hie' },
    { title: 'Peak Training Load', signatureTitle: 'Peak Power (PP)', metric: 'peak', sigKey: 'pp' },
  ];

  // What the TP/HIE/PP cards plot; the Total card always shows its training load.
  let view: ChartView = loadView();
  $: saveView(view);

  // A silent load refreshes in place: no dimming, and a failure keeps the data already on screen.
  async function load(silent = false) {
    const id = ++requestId;
    // An empty selection is not sent: the server treats a missing years[] as "use the default years".
    if (selectedYears?.length === 0) {
      data = null;
      error = null;
      loading = false;
      return;
    }
    if (!silent) loading = true;
    error = null;
    try {
      const response = await getMySeasonsData(startMonth, endMonth, selectedYears);
      if (id !== requestId) return; // A newer selection superseded this request.
      data = response;
      yearsAvailable = response.years_available;
      nextYearAvailable = response.next_year_available;
      selectedYears = response.years;
    } catch (e) {
      if (id !== requestId) return;
      if (!silent) error = 'Unable to load your seasons. Please try again.';
    } finally {
      if (id === requestId) loading = false;
    }
  }

  function onMonths(e: CustomEvent<{ startMonth: number; endMonth: number }>) {
    ({ startMonth, endMonth } = e.detail);
    checkpoint.set(null); // x positions mean different dates under a new range
    saveSettings(startMonth, endMonth, null);
    load();
  }

  // Also drops the shared hover position: on touch screens it is kept after a tap, and would otherwise leave a stale guide behind.
  function clearCheckpoint() {
    checkpoint.set(null);
    hover.set({ x: null, from: '' });
  }

  // On touch screens the hover guide and day tooltips stay after a tap (there's no pointer-out); a tap anywhere
  // outside the charts puts them away. The checkpoint stays.
  function onPointerDown(e: PointerEvent) {
    if (e.pointerType !== 'touch' || $hover.x == null) return;
    if ((e.target as Element | null)?.closest?.('canvas')) return;
    hover.set({ x: null, from: '' });
  }

  function onYears(e: CustomEvent<number[]>) {
    selectedYears = e.detail;
    load();
  }

  // Trickle-down rewrites the training loads and signatures behind every season, so reload once it completes.
  // The notification arrives the moment the job finishes, and a short debounce collapses any burst into one refetch.
  const trickleDebouncer = debouncer(400);
  const onTrickleComplete = () => trickleDebouncer.debounce(() => load(true));

  onMount(() => {
    load();
    //@ts-ignore
    const channel = window.Xert?._userChannel;
    channel?.bind('trickle_complete', onTrickleComplete);
    return () => {
      channel?.unbind('trickle_complete', onTrickleComplete);
      trickleDebouncer.clear();
    };
  });
</script>

<svelte:window on:pointerdown|capture={onPointerDown} />

<div class="mx-auto flex w-full max-w-[1600px] flex-col gap-4 p-4">
  <h1 class="text-2xl font-semibold">My Seasons</h1>

  <SeasonControls {startMonth} {endMonth} {yearsAvailable} {nextYearAvailable} selectedYears={selectedYears ?? []} checkpointX={$checkpoint} on:months={onMonths} on:years={onYears} on:clearCheckpoint={clearCheckpoint} />

  {#if error}
    <p class="text-sm text-red-600">{error}</p>
  {/if}

  {#if data && selectedYears?.length}
    <!-- Two shared columns on wide screens: the chart column is the same for every card, the table column fits the widest table. -->
    <div class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(320px,1fr)_minmax(0,max-content)]" class:opacity-60={loading}>
      {#each cards as card, i (card.metric)}
        {#if i === 1}
          <!-- Switches the TP/HIE/PP cards below between their training load and predicted signature. -->
          <div class="flex items-center gap-3 text-sm xl:col-span-2">
            <button class="view-label" class:active={view === 'signature'} on:click={() => (view = 'signature')}>Fitness Signature</button>
            <Switch
              checked={view === 'load'}
              onCheckedChange={(checked) => (view = checked ? 'load' : 'signature')}
              aria-label="Show training loads instead of fitness signature"
            />
            <button class="view-label" class:active={view === 'load'} on:click={() => (view = 'load')}>Training Loads</button>
          </div>
        {/if}
        <SeasonCard
          title={view === 'signature' && card.signatureTitle ? card.signatureTitle : card.title}
          {view}
          seasons={data.seasons}
          years={data.years}
          metric={card.metric}
          sigKey={card.sigKey}
          {startMonth}
          {endMonth}
          {distanceUnits}
          {hover}
          {checkpoint}
        />
      {/each}
    </div>
  {:else if loading}
    <Skeleton class="h-[300px] w-full" />
  {:else if !error}
    <p class="text-sm text-muted-foreground">Select at least one year to see its season.</p>
  {/if}
</div>

<style>
  .view-label {
    background: transparent;
    border: 0;
    padding: 0;
    color: hsl(var(--muted-foreground));
  }
  .view-label.active {
    color: hsl(var(--foreground));
    font-weight: 600;
  }
</style>
